import host from '../host';
import { isPortalFlavor } from '../appFlavor';

// Native chrome differs per app flavor (both binaries build from this bundle;
// see appFlavor.js / App.js, which picks the shell the same way):
//
//  * keep-awake — the POS is a till that must stay lit through a whole shift
//    with no touch input between orders. The Manager app is a back-office app
//    a manager glances at, so holding the screen on there is pure battery
//    drain with no benefit. POS only.
//
//  * status-bar style — the plugin's enum is named for the BACKGROUND, not the
//    text: Style.Dark ("DARK") is documented as "Light text for dark
//    backgrounds" and Style.Light ("LIGHT") as "Dark text for light
//    backgrounds". The POS chrome is a dark navy bar, so it wants Style.Dark
//    (light text). The Manager app is light-only (no dark-mode toggle is
//    reachable in portal-app; surface is --c-bg #f6f7f9), so Style.Dark would
//    render near-invisible light text there — it wants Style.Light.
//
//  * viewport scale lock — see lockViewportScale() below. POS only.

// Directives we force onto the viewport meta tag for the native till.
const SCALE_LOCK = { 'maximum-scale': '1', 'user-scalable': 'no' };

// Pinch-zoom on the native POS is a bug, not a feature.
//
// public/index.html ships `width=device-width, initial-scale=1,
// viewport-fit=cover` with no maximum-scale, so pinch-zoom is enabled — and on
// iOS it is STICKY: one accidental two-finger brush against the screen mid-
// service leaves the till zoomed, with the settings gear and the bottom tab bar
// pushed off the visible area. There is no obvious way back for a cashier who
// doesn't know to pinch out, and the layout is fixed-height chrome, so nothing
// reflows to rescue it. Confirmed in QA on a real iPhone.
//
// ACCESSIBILITY TRADE-OFF — disabling user zoom is normally a WCAG 1.4.4
// failure and we are not doing it lightly. It is applied at RUNTIME, and only
// for the native POS flavour, precisely so the trade-off stays scoped:
//
//   * The POS is a fixed-purpose kiosk. It runs full-screen on a device the
//     business owns, dedicated to one task, at a fixed viewing distance, with a
//     layout already sized for touch at that distance. There is no long-form
//     content to magnify — zooming only ever breaks the chrome. Text scaling
//     for staff who need it belongs in the POS's own type scale, not in a
//     browser gesture that can strand the UI.
//   * The Manager Portal is a content app — reports, tables, dense figures,
//     read on whatever device the manager happens to hold. There, pinch-zoom is
//     a legitimate and necessary accessibility affordance and must be preserved.
//
// Both surfaces share public/index.html, which is why this cannot be a static
// meta tag: editing that file would strip zoom from the portal too. Hence a
// runtime rewrite gated on capacitor AND the non-portal flavour.
//
// Existing directives (width, initial-scale, viewport-fit=cover — the last is
// load-bearing for safe-area insets on notched devices) are preserved; we only
// add/override the two scale keys. Returns true if the tag was rewritten.
export function lockViewportScale() {
  const meta = typeof document !== 'undefined'
    ? document.querySelector('meta[name="viewport"]')
    : null;
  // No viewport tag (some test/embed contexts) — nothing to lock, and creating
  // one would be a bigger change to layout than the bug it fixes.
  if (!meta) return false;

  const directives = [];
  const seen = new Set();
  for (const part of (meta.getAttribute('content') || '').split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.split('=')[0].trim().toLowerCase();
    seen.add(key);
    // Re-emit ours canonically if index.html ever starts declaring them.
    // hasOwnProperty, not `in`: `in` would match inherited keys like toString.
    const ours = Object.prototype.hasOwnProperty.call(SCALE_LOCK, key);
    directives.push(ours ? `${key}=${SCALE_LOCK[key]}` : trimmed);
  }
  for (const [key, value] of Object.entries(SCALE_LOCK)) {
    if (!seen.has(key)) directives.push(`${key}=${value}`);
  }

  meta.setAttribute('content', directives.join(', '));
  return true;
}

export async function initNativeChrome() {
  if (host.type !== 'capacitor') return;


  if (!isPortalFlavor) {
    // Kiosk-only: never on the portal flavour, where zoom is an a11y feature.
    lockViewportScale();

    const { KeepAwake } = await import('@capacitor-community/keep-awake'); // official pkg name (no @capacitor/keep-awake exists)
    try { await KeepAwake.keepAwake(); } catch {}
  }

  const { StatusBar, Style } = await import('@capacitor/status-bar');
  // Dark = light text (dark bg) for the POS; Light = dark text (light bg) for Manager.
  const style = isPortalFlavor ? Style.Light : Style.Dark;
  try { await StatusBar.setStyle({ style }); } catch {}
}

// Maps our 'landscape'|'portrait' modes to the plugin's more specific
// orientation type (locks to either "primary" variant — device rotation
// between landscape-primary/-secondary, or portrait-primary/-secondary,
// stays free).
const PLUGIN_ORIENTATION = { landscape: 'landscape-primary', portrait: 'portrait-primary' };

// Orientation is set per station in Phase 6 via setOrientation('landscape'|'portrait').
// window.screen.orientation.lock is unsupported in iOS WKWebView (silently
// rejects there), so on capacitor we lock via the native
// @capacitor/screen-orientation plugin instead — it works on both iOS and
// Android. The web Orientation API is kept as a fallback for whatever the
// plugin doesn't cover (and so this stays a no-op with no plugin installed).
export async function setOrientation(mode) {
  if (host.type !== 'capacitor') return;

  if (mode == null) {
    try {
      const { ScreenOrientation } = await import('@capacitor/screen-orientation');
      await ScreenOrientation.unlock();
      return;
    } catch {
      try { window.screen?.orientation?.unlock?.(); } catch {}
      return;
    }
  }

  const pluginType = PLUGIN_ORIENTATION[mode];
  if (pluginType) {
    try {
      const { ScreenOrientation } = await import('@capacitor/screen-orientation');
      await ScreenOrientation.lock({ orientation: pluginType });
      return;
    } catch {
      // Plugin missing/unsupported — fall through to the web API below.
      // (F12a-N Issue B) On iPad multitasking (Split View/Slide Over) this
      // rejects with "current windowing mode does not allow programmatic
      // changes" — expected there, so this stays a silent catch (no
      // console.error/warn). UIRequiresFullScreen (Info.plist) removes
      // multitasking entirely so this path shouldn't fire in production,
      // but the swallow stays regardless as defence in depth. Callers
      // (CashierStation/WaiterStation/KdsStation) never await/.catch this
      // promise themselves, so nothing upstream can re-surface it either.
    }
  }

  try {
    if (mode === 'landscape') window.screen?.orientation?.lock?.('landscape');
    else if (mode === 'portrait') window.screen?.orientation?.lock?.('portrait');
    else window.screen?.orientation?.unlock?.();
  } catch {}
}
